import { useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Loader2,
  Send,
} from "lucide-react";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, errMsg } from "../lib/api";
import { useBranding } from "../lib/branding";
import { waNumber, waContactLink } from "./WhatsAppFab";

const isPublicContactValue = (value) => {
  const text = String(value || "").trim();
  return Boolean(text) && !text.includes("[") && !/^test_/i.test(text);
};

const toDate = (value) => (value ? new Date(`${value}T00:00:00`) : undefined);

function QuoteSelect({ value, onValueChange, items, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-12 bg-white" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item} value={item}>
            {item}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ContactSection() {
  const b = useBranding() || {};
  const [f, setF] = useState({
    name: "",
    phone: "",
    email: "",
    project: "",
    location: "",
    propertyType: "Apartment",
    bhk: "2 BHK",
    date: "",
  });
  const [busy, setBusy] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.phone.replace(/\D/g, "").length < 8) {
      setPhoneError("Enter a valid mobile number.");
      return;
    }
    setBusy(true);
    try {
      const message = [
        `Project: ${f.project || "Not specified"}`,
        `Location: ${f.location || "Not specified"}`,
        `Property type: ${f.propertyType}`,
        `Configuration: ${f.bhk}`,
        f.date && `Preferred date: ${f.date}`,
      ]
        .filter(Boolean)
        .join("\n");
      await api.post("/public/enquiry", {
        name: f.name,
        phone: f.phone,
        email: f.email,
        message,
      });
      toast.success("Thank you! Our team will call you shortly.");
      setF({
        name: "",
        phone: "",
        email: "",
        project: "",
        location: "",
        propertyType: "Apartment",
        bhk: "2 BHK",
        date: "",
      });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const hasWa = waNumber(b).length >= 10;
  const hasPhone = (b.phone || "").replace(/\D/g, "").length >= 10;
  const hasEmail = isPublicContactValue(b.email) && b.email.includes("@");
  const hasAddress = isPublicContactValue(b.address);
  return (
    <section
      id="contact"
      className="scroll-mt-16 border-t border-slate-200 bg-white py-20"
      data-testid="contact-section"
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-2">
        <div>
          <div className="overline mb-3">Contact us</div>
          <h2 className="font-display text-3xl font-extrabold sm:text-4xl">
            Get your personalised inspection quote.
          </h2>
          <p className="mt-3 max-w-md text-slate-600">
            Tell us a little about your property and we will help you plan the
            right inspection.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            {hasPhone && (
              <div
                className="flex items-center gap-3"
                data-testid="contact-phone"
              >
                <Phone className="h-4 w-4 text-teal-600" />
                {b.phone}
              </div>
            )}
            {hasEmail && (
              <div
                className="flex items-center gap-3"
                data-testid="contact-email"
              >
                <Mail className="h-4 w-4 text-teal-600" />
                {b.email}
              </div>
            )}
            {hasAddress && (
              <div
                className="flex items-center gap-3"
                data-testid="contact-address"
              >
                <MapPin className="h-4 w-4 text-teal-600" />
                {b.address}
              </div>
            )}
          </div>
          {hasWa ? (
            <a
              href={waContactLink(b)}
              target="_blank"
              rel="noreferrer"
              className="tap mt-8 inline-flex h-12 items-center gap-2 rounded-md bg-[#25D366] px-6 font-semibold text-white hover:bg-[#1ebe5a]"
              data-testid="contact-whatsapp-button"
            >
              <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
            </a>
          ) : null}
        </div>
        <form
          onSubmit={submit}
          className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-6"
          data-testid="contact-form"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              required
              className="h-12 bg-white"
              placeholder="Your name"
              value={f.name}
              onChange={set("name")}
              data-testid="contact-name-input"
            />
            <Input
              required
              className="h-12 bg-white"
              placeholder="Phone"
              value={f.phone}
              onChange={(event) => {
                set("phone")(event);
                setPhoneError("");
              }}
              aria-describedby={phoneError ? "contact-phone-error" : undefined}
              data-testid="contact-phone-input"
            />
          </div>
          {phoneError && (
            <p
              id="contact-phone-error"
              className="text-sm text-rose-600"
              data-testid="contact-phone-error"
            >
              {phoneError}
            </p>
          )}
          <Input
            type="email"
            className="h-12 bg-white"
            placeholder="Email (optional)"
            value={f.email}
            onChange={set("email")}
            data-testid="contact-email-input"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              className="h-12 bg-white"
              placeholder="Property / project name"
              value={f.project}
              onChange={set("project")}
              data-testid="contact-project-input"
            />
            <Input
              className="h-12 bg-white"
              placeholder="Location"
              value={f.location}
              onChange={set("location")}
              data-testid="contact-location-input"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <QuoteSelect
              value={f.propertyType}
              onValueChange={(propertyType) =>
                setF((x) => ({ ...x, propertyType }))
              }
              testid="contact-property-type-select"
              placeholder="Property type"
              items={["Apartment", "Villa", "Independent house", "Other"]}
            />
            <QuoteSelect
              value={f.bhk}
              onValueChange={(bhk) => setF((x) => ({ ...x, bhk }))}
              testid="contact-bhk-select"
              placeholder="Configuration"
              items={[
                "1 BHK",
                "1.5 BHK",
                "2 BHK",
                "2.5 BHK",
                "3 BHK",
                "3.5 BHK",
                "4 BHK",
                "Villa",
                "Other",
              ]}
            />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="tap flex h-12 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm text-slate-600"
                  data-testid="contact-date-input"
                >
                  <CalendarDays className="h-4 w-4 text-teal-700" />
                  {f.date || "Preferred date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={toDate(f.date)}
                  onSelect={(date) =>
                    setF((x) => ({
                      ...x,
                      date: date?.toISOString().slice(0, 10) || "",
                    }))
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <button
            disabled={busy}
            className="tap flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-teal-700"
            data-testid="contact-submit-button"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}{" "}
            Get my inspection quote
          </button>
        </form>
      </div>
    </section>
  );
}
